import { useState, useEffect } from 'react';
import { useRouter } from 'next/router';
import Link from 'next/link';
import { captureException } from '@sentry/nextjs';
import axios from 'axios';
import { Markup } from 'interweave';
import { useAtom } from 'jotai';
import {
  authAtom,
  addToCartErrorAtom,
  productVariantsAtom,
  productImagesAtom
} from 'global/store';
import { Box } from '@xstyled/styled-components';
import useProductPrice from 'hooks/products/useProductPrice';
import useColors from 'hooks/products/useColors';
import useAddToCart from 'hooks/cart/useAddToCart';
import ImageSlider from '../ImageCarousel/Slider';
import ImageSwiper from '../ImageCarousel/Swiper';
import ColorSlider from '../ColorSelect/Slider';
import ColorSwiper from '../ColorSelect/Swiper';
import StockStatus from './StockStatus';
import CustomOptions from './CustomOptions';
import Favorite from './Favorite';
import PrimaryButton from '@/UI/Button/Variants/Primary';
import { SectionTitle } from '@/UI/Primitives/Headings';
import StarRating from '@/UI/Ratings/StarRating';
import { FlexBox, FlexColCenter } from '@/UI/Primitives/FlexBox';
import Spinner from '@/UI/Loader/Spinner';
import { DeliveryTruck } from '@/UI/Icons/SingleProduct';
import { PriceInformation, ProductDetails, ProductInformation } from './styles';

import { useTranslation } from 'next-i18next';
import brandsData from '../../../data/brands.json';

export default function MainDetails({ product }) {
  const router = useRouter();
  const { t } = useTranslation();

  const { media_gallery, rating_summary } = product || {};

  const brand = product?.mpbrand;
  const brandValues = brand?.value && brand?.image;
  const configurableOptions = product?.configurable_options;

  const [stockStatus, setStockStatus] = useState(null);
  const [inStock, setInStock] = useState(false);
  const [deliveryTime, setDeliveryTime] = useState(null);
  const [priceRange, setPriceRange] = useState(product?.price_range);
  const [variation, setVariation] = useState(false);
  const [images, setImages] = useAtom(productImagesAtom);
  const [brandImage, setBrandImage] = useState();
  
  // Add new state for better stock tracking
  const [currentSku, setCurrentSku] = useState(product?.sku);
  const [isCheckingStock, setIsCheckingStock] = useState(false);

  const [selectedVariants, setSelectedVariants] = useAtom(productVariantsAtom);

  const [{ token: isAuth }] = useAtom(authAtom);
  const [error] = useAtom(addToCartErrorAtom);

  const price = useProductPrice(priceRange);

  const { showColors, colors } = useColors(product);

  const { addToCart, loading } = useAddToCart(product);

  // Helper function to get the current active SKU
  const getCurrentSku = () => {
    if (!variation || !variation.sku) {
      return product?.sku;
    }
    return variation.sku;
  };

  // Helper function to check if all required configurable options are selected
  const areAllOptionsSelected = () => {
    if (!configurableOptions || configurableOptions.length === 0) {
      return true; // Simple product, no options required
    }
    
    if (!selectedVariants || selectedVariants.length === 0) {
      return false; // No variants selected
    }
    
    // Check if the number of selected variants matches the number of configurable options
    const requiredOptionsCount = configurableOptions.length;
    const selectedOptionsCount = selectedVariants.length;
    
    
    return selectedOptionsCount === requiredOptionsCount;
  };

  useEffect(() => {
    if (brand && brand.option_id) {
      const brandID = brand.option_id;
      const index = brandsData.findIndex(el => el.option_id === brandID);
      if (index !== -1) {
        setBrandImage(brandsData[index].image);
      } else {
        setBrandImage(brand.image);
      }
    }
  }, [brand]);

  useEffect(() => {
    if (selectedVariants?.length > 0) {
      let variant;

      if (configurableOptions?.length > 1) {
        variant = product?.variants?.find(({ attributes }) => {
          return attributes?.find(
            item => item?.uid === selectedVariants[0]?.uid
          );
        })?.product;
      } else {
        variant = product?.variants?.find(
          ({ product }) => product?.sku === selectedVariants[0]?.sku
        )?.product;
      }
      
      if (!variant) {
        variant = product?.variants?.find(({ attributes }) => {
          return attributes?.find(
            item => item?.uid === selectedVariants[0]?.uid
          );
        });
        if (variant) {
          variant = variant?.product;
        }
      }

      if (configurableOptions.length > 1 && selectedVariants.length > 1) {
        const filterValue = selectedVariants.map(obj => obj.uid);
        const options = Array.from(product?.variants);
        const possibleOptions = options.filter(obj =>
          filterValue.every(uuid =>
            obj.attributes.some(valueObj => valueObj.uid === uuid)
          )
        );

        if (possibleOptions.length > 0) {
          variant = possibleOptions[0]?.product;
          setImages(possibleOptions[0]?.product?.media_gallery);
        }
      }

      variant && setPriceRange(variant?.price_range);
      variant && setVariation(variant);
    } else {
      setVariation(false);
    }
  }, [configurableOptions, selectedVariants]);

  useEffect(() => {
    if (selectedVariants?.length === 0) {
      media_gallery && setImages(media_gallery);
      setPriceRange(product?.price_range);
    }
  }, [media_gallery, selectedVariants]);

  // Separate effect to track the current SKU
  useEffect(() => {
    const activeSku = getCurrentSku();
    setCurrentSku(activeSku);
  }, [variation, product?.sku]);

  // Use the stockStatus from StockStatus component to determine in stock and delivery time
  useEffect(() => {
    // For configurable products, check if all required options are selected
    if (configurableOptions && configurableOptions.length > 0) {
      if (!areAllOptionsSelected()) {
        setIsCheckingStock(false);
        setInStock(false);
        setDeliveryTime(null); // Clear delivery time when not all options selected
        return;
      }
    }

    if (!stockStatus) {
      setIsCheckingStock(true);
      return;
    }

    setIsCheckingStock(false);

    // Calculate total stock from both warehouses
    const kvartalQty = stockStatus?.kvartal?.qty || 0;
    const warehouseQty = stockStatus?.warehouse?.qty || 0;
    const totalQty = kvartalQty + warehouseQty;
    const hasStock = totalQty > 0;


    setInStock(hasStock);

    if (!hasStock) {
      
      axios
        .post('/api/delivery-time', { sku: currentSku, skuMain: product?.sku })
        .then(deliveryResponse => {
          const deliveryData = deliveryResponse.data;
          
          if (deliveryData && deliveryData !== '' && deliveryData !== null) {
            setDeliveryTime(deliveryData);
          } else {
            setDeliveryTime(null);
          }
        })
        .catch(error => {
          console.error('Error fetching delivery time:', error); // Debug log
          captureException(error);
          setDeliveryTime(null);
        });
    } else {
      // Has stock, clear delivery time
      setDeliveryTime(null);
    }

  }, [stockStatus, currentSku, product?.sku, configurableOptions, selectedVariants]); // Add selectedVariants as dependency

  useEffect(() => {
    const handleRouteChange = () => setSelectedVariants([]);
    router.events.on('routeChangeStart', handleRouteChange);
    return () => router.events.off('routeChangeStart', handleRouteChange);
  }, []);

  // Update the delivery time display logic
  const shouldShowDeliveryTime = !inStock && deliveryTime && !isCheckingStock && 
    // Don't show delivery time if not all configurable options are selected
    areAllOptionsSelected();
  
  const shouldShowInStock = inStock && !isCheckingStock && 
    // Don't show in stock if not all configurable options are selected
    areAllOptionsSelected();

  return (
    <ProductDetails>
      <Box minWidth={81}>
        <ImageSlider images={images} product={product?.name} />
      </Box>

      <ProductInformation error={error}>
        {brandValues && (
          <Link href={`/brands/${brand?.url_key}?id=${brand?.option_id}`}>
            <a className="brand_logo">
              <img
                src={brandImage}
                alt={brand?.value}
                layout="fill"
                objectFit="contain"
              />
            </a>
          </Link>
        )}

        <SectionTitle
          as="h1"
          fontSize="large"
          pt={{ desktop: brandValues ? '3rem' : 0 }}
          pb={{ _: '1rem', desktop: rating_summary > 0 ? '0.5rem' : '1rem' }}
        >
          {product?.name}
        </SectionTitle>

        {rating_summary > 0 && (
          <FlexBox spaceX="0.75rem" pb={{ _: '1.5rem', desktop: '2rem' }}>
            <span>Kasutajate hinnang: </span>
            <StarRating
              rating={rating_summary}
              spaceX="0.25rem"
              transform="translateY(5%)"
            />
          </FlexBox>
        )}

        <Box display={{ _: 'block', desktop: 'none' }} pb="2rem">
          <ImageSwiper images={images} product={product?.name} />
          {showColors && <ColorSwiper variants={colors} />}
        </Box>

        <Box display={{ _: 'block', desktop: 'none' }}>
          {configurableOptions && <CustomOptions product={product} />}
        </Box>

        <Markup content={product?.short_description?.html} tagName="p" />

        <StockStatus
          id={product?.id}
          sku={product?.sku}
          stockStatus={stockStatus}
          setStockStatus={setStockStatus}
          product={product}
        />

        {price?.value && (
          <PriceInformation>
            <div>
              {t('product_main.current_price')}
              <br />
              {price?.value}
            </div>
            {price?.discount?.value && (
              <span ml={'2rem'}>
                {t('product_main.regular_price')} <br />{' '}
                <span>{price?.discount?.value}</span>
              </span>
            )}
          </PriceInformation>
        )}

        {showColors && (
          <Box
            display={{ _: 'none', desktop: 'block' }}
            pb={configurableOptions ? 0 : '2rem'}
          >
            <h4>{t('product_main.color_picker')}:</h4>
            <ColorSlider variants={colors} />
          </Box>
        )}

        {configurableOptions && (
          <Box display={{ _: 'none', desktop: 'block' }}>
            <CustomOptions product={product} />
          </Box>
        )}

        {shouldShowDeliveryTime && (
          <FlexBox align="center" spaceX="1rem" pb="2rem">
            <span role="img" aria-label="Delivery truck icon">
              {DeliveryTruck}
            </span>
            <span>
              {t('product_main.delivery_time')}: {deliveryTime}{' '}
              {t('product_main.days')}
            </span>
          </FlexBox>
        )}

        {shouldShowInStock && (
          <p>{t('product_main.in_stock')}</p>
        )}

        {isCheckingStock && (
          <p>Checking availability...</p>
        )}

        <FlexColCenter
          maxW={400}
          align={{ _: 'flex-start', desktop: 'center' }}
          ml={{ _: 0, desktop: '-3rem' }}
        >
          <PrimaryButton
            w="80%"
            py="0.5rem"
            mb={{ _: '1rem', desktop: '1.5rem' }}
            onClick={addToCart}
          >
            {loading ? <Spinner /> : t('product_main.basket_add')}
          </PrimaryButton>
          <FlexBox spaceX="1rem">
            {isAuth && <Favorite product={product} />}
          </FlexBox>
        </FlexColCenter>
      </ProductInformation>
    </ProductDetails>
  );
}